/**
 * The full list behind each container on the record: every commitment, every candidate, every
 * cadence. A table rather than cards, because the job of a list page is to find one line again
 * and compare it with its neighbours: the item, the contract line that funds it, where it sits,
 * its date and where it stands. Same actions as the record, in the last column; whatever the
 * action needs said opens under the row it belongs to.
 */
import { useState } from "preact/hooks";
import type { RecordLine, RecordView } from "@trellis/core";
import { fmtDate, isCadenceItem, plural } from "@trellis/core";
import { Link } from "../components/link.js";
import { Tip } from "../components/tip.js";
import { AttentionWhy, ClinTagView, RowButtons, RowForms, itemHref, sourceLine, useRow, type OnAction, type Person } from "./record.js";

export type ListKind = "commitments" | "candidates" | "cadences";

const ABOUT: Record<ListKind, { title: string; lede: string; empty: string }> = {
  commitments: {
    title: "Commitments",
    lede: "Everything on the record inside the window: open, closed, set aside and declined. A closed line keeps its note and its source, and a set-aside can be restored from here.",
    empty: "Nothing on this record yet.",
  },
  candidates: {
    title: "Candidates",
    lede: "Things Trellis heard and has not been told about. Nothing here is on the record until it is confirmed, and nobody else sees what it came from.",
    empty: "Nothing waiting.",
  },
  cadences: {
    title: "Cadences",
    lede: "The recurring reports and meetings the delivery model says fall to this person. Closing one records that it happened.",
    empty: "No cadence falls here this period.",
  },
};

/** The filters each list offers, as a count over the whole list. `undefined` keeps everything. */
const FILTERS: Record<ListKind, { id: string; label: string; keep: (l: RecordLine) => boolean }[]> = {
  commitments: [
    { id: "open", label: "Open", keep: (l) => l.status === "open" },
    { id: "late", label: "Late", keep: (l) => l.outcome.kind === "overdue" || l.outcome.kind === "closed-late" },
    { id: "closed", label: "Closed", keep: (l) => l.status === "closed" },
  ],
  candidates: [
    { id: "proposed", label: "Proposed to you", keep: (l) => l.actions.includes("accept") || l.actions.includes("accept-transfer") },
  ],
  cadences: [
    { id: "due", label: "Due", keep: (l) => l.status === "open" },
  ],
};

const HEADS: Record<ListKind, { label: string; what: string; cls?: string }[]> = {
  commitments: [
    { label: "Item", what: "The commitment, and where Trellis read it." },
    { label: "Line", what: "The contract line that funds this work." },
    { label: "Unit", what: "The team or workstream the work sits on." },
    { label: "Due", what: "The date the owner accepted." },
    { label: "State", what: "Where it is now." },
  ],
  candidates: [
    { label: "Item", what: "What Trellis heard, and where it read it." },
    { label: "Line", what: "A candidate sits on no unit, so it has no contract line yet." },
    { label: "From", what: "Who proposed it, or what it was read from." },
    { label: "Date", what: "The date it states. You can change it before you accept." },
    { label: "State", what: "How long it has been waiting for you." },
  ],
  cadences: [
    { label: "Cadence", what: "The recurring obligation." },
    { label: "Line", what: "The contract line that funds it." },
    { label: "Scope", what: "The program or unit it runs over." },
    { label: "Due", what: "When this run is due." },
    { label: "State", what: "Where this run is." },
  ],
};

/** Says the same number the dashboard box says, then the size of the whole list. Nothing
    at all when the list is empty: the panel below already says so in a sentence. */
function tally(kind: ListKind, all: RecordLine[]): string {
  if (all.length === 0) return "";
  if (kind !== "commitments") return plural(all.length, "line");
  const open = all.filter((l) => l.status === "open").length;
  return `${open} open · ${plural(all.length, "line")} in the window`;
}

export function ListScreen({ kind, record, onAction, peopleOf, notice }: {
  kind: ListKind; record: RecordView; onAction?: OnAction | undefined; peopleOf?: ((programId: string) => Promise<Person[]>) | undefined;
  /** What the last action said, or why it was refused. */
  notice?: { text: string; error?: boolean } | undefined;
}) {
  const [q, setQ] = useState("");
  const [only, setOnly] = useState<string | undefined>(undefined);
  const mine = record.viewerIsOwner;
  const about = ABOUT[kind];
  // A cadence line is also on `lines`; it belongs to its own list, not to this one.
  const all = kind === "cadences" ? record.cadences
    : kind === "candidates" ? (record.candidates ?? [])
    : record.lines.filter((l) => !isCadenceItem(l));
  const filters = FILTERS[kind].map((f) => ({ ...f, n: all.filter(f.keep).length })).filter((f) => f.n > 0);
  const chosen = filters.find((f) => f.id === only);
  const needle = q.trim().toLowerCase();
  const lines = all
    .filter((l) => !chosen || chosen.keep(l))
    .filter((l) => !needle || `${l.title} ${l.programName ?? ""} ${l.unitName ?? ""}`.toLowerCase().includes(needle));
  const back = mine ? "/app" : `/app/people/${encodeURIComponent(record.owner)}`;
  return (
    <main class="main wide list-page">
      <p class="s"><Link class="link" href={back}>{mine ? "Your record" : record.ownerName}</Link></p>
      <h1 class="h-lg" style="margin-top:4px">{about.title}</h1>
      <p class="lede">{about.lede}</p>
      {notice && <div class={`notice${notice.error ? " error" : ""}`}>{notice.text}</div>}

      <div class="filters">
        {filters.length > 0 && (
          <span class="seg" role="group" aria-label="Filter">
            <button type="button" aria-pressed={!only} onClick={() => setOnly(undefined)}>All {all.length}</button>
            {filters.map((f) => (
              <button key={f.id} type="button" aria-pressed={only === f.id} onClick={() => setOnly(only === f.id ? undefined : f.id)}>{f.label} {f.n}</button>
            ))}
          </span>
        )}
        <span class="grow" />
        {all.length > 6 && (
          <label class="list-find">
            <span class="sr-only">Find a line</span>
            <input type="search" value={q} placeholder="Part of a title, a unit or a programme" onInput={(e) => setQ((e.currentTarget as HTMLInputElement).value)} />
          </label>
        )}
        {tally(kind, all) && <span class="muted list-tally">{tally(kind, all)}</span>}
      </div>

      <section class="panel list-panel">
        {lines.length === 0
          ? <p class="tile-empty">{needle ? `Nothing matches “${q.trim()}”.` : chosen ? `Nothing ${chosen.label.toLowerCase()}.` : about.empty}</p>
          : (
            <div class="grid-wrap">
              <table class="grid">
                <thead>
                  <tr>
                    {HEADS[kind].map((h) => <th key={h.label} scope="col"><Tip what={h.what}>{h.label}</Tip></th>)}
                    <th scope="col"><span class="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => <ListRow key={l.id} kind={kind} line={l} owner={record.owner} mine={mine} onAction={onAction} peopleOf={peopleOf} />)}
                </tbody>
              </table>
            </div>
          )}
      </section>
      <p class="note dim list-foot">Counts, never ranks. Nothing on this page scores a person.</p>
    </main>
  );
}

function ListRow({ kind, line, owner, mine, onAction, peopleOf }: {
  kind: ListKind; line: RecordLine; owner: string; mine: boolean; onAction?: OnAction | undefined; peopleOf?: ((programId: string) => Promise<Person[]>) | undefined;
}) {
  const state = useRow({ line, mine, onAction, peopleOf });
  const cols = HEADS[kind].length + 1;
  // Below the desktop breakpoint each row becomes a block, which takes the table's own
  // header associations with it. The label is real text rather than generated content so a
  // screen reader still hears what each value is.
  const label = (text: string) => <span class="cell-label">{text}: </span>;
  const heads = HEADS[kind];
  return (
    <>
      <tr id={`c-${line.id}`}>
        <td>
          <span class="grid-t"><Link class="item-open" href={itemHref(line.id, owner, mine)}>{line.title}</Link></span>
          <AttentionWhy line={line} />
          <span class="note dim grid-src">{sourceLine(line)}</span>
        </td>
        <td>{label(heads[1]!.label)}<ClinTagView clin={line.clin} /></td>
        <td class="note">
          {label(heads[2]!.label)}
          {kind === "candidates" ? (line.askedBy ?? line.programName ?? "—") : (line.unitName ?? line.programName ?? "—")}
        </td>
        <td class="note num">{label(heads[3]!.label)}{fmtDate(line.when)}</td>
        <td class={`note tone-${line.outcome.tone}`}>{label(heads[4]!.label)}{line.outcome.label}</td>
        <td class="grid-acts">{!state.form && <RowButtons state={state} />}</td>
      </tr>
      {state.form && (
        <tr class="grid-form"><td colSpan={cols}><RowForms state={state} /></td></tr>
      )}
    </>
  );
}
